import type { ContextType } from "react";
import { Component } from "react";

import autobind from "autobind-decorator";
import findKey from "lodash/findKey";
import styled from "styled-components";

import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Button, ButtonLabel, IconBoxWithTextSwitchItem, Switcher } from "@similarweb/ui-components";

import { AssetsService } from "app/services/AssetsService";

import { ProModal } from "../../Modals/src/ProModal";
import { TrackContext } from "../../WithTrack/src/context";
import WithTranslation from "../../WithTranslation/src/WithTranslation";

interface IBGImage {
    imgSrc: string;
    height?: number;
}

const BgImage = styled.div<IBGImage>`
    background-image: url(${({ imgSrc }) => AssetsService.assetUrl(imgSrc)});
    background-repeat: no-repeat;
    flex: 1;
    height: ${({ height }) => (height ? `${height}px` : `432px`)};
`;

interface IWorkspace {
    name: string;
    id: number;
}

interface ISelectWorkspaceTrialModalProps {
    isOpen: boolean;
    onCloseClick: () => void;
    createWorkspaceClick: (selectedWorkspace: IWorkspace) => void;
}

enum ModalStep {
    select = "SELECT",
    create = "CREATE",
}

export const Workspaces = {
    investors: { name: "investors", id: 0 },
    marketing: { name: "marketing", id: 1 },
    sales: { name: "sales", id: 2 },
};

const step1 = {
    customStyles: {
        content: {
            boxSizing: "content-box",
            width: "450px",
        },
    },
};

const step2 = {
    customStyles: {
        content: {
            width: "640px",
        },
    },
};

export class SelectWorkspaceTrialModal extends Component<ISelectWorkspaceTrialModalProps, any> {
    static contextType = TrackContext;

    context: ContextType<typeof TrackContext>;

    constructor(props) {
        super(props);
        this.state = {
            modalStep: ModalStep.create,
            selectedWorkspace: Workspaces.marketing,
        };
    }

    public onItemClick = (value) => {
        const workspaceKey = findKey(Workspaces, (workspace: { name: string; id: number }) => workspace.id === value);
        const selectedWorkspace = Workspaces[workspaceKey];
        this.setState({
            selectedWorkspace,
            modalStep: ModalStep.create,
        });
    };

    @autobind
    public createWorkspaceClick() {
        this.props.createWorkspaceClick(this.state.selectedWorkspace);
        this.context.track(
            "Try workspace  wizard",
            "click",
            `Third screen/get started/${this.state.selectedWorkspace.name}`,
        );
    }

    public render() {
        const { isOpen } = this.props;
        const { modalStep } = this.state;
        const modalText = {
            SELECT: {
                title: "workspaces.trial.create.modal.step1.title",
                subtitle: "workspaces.trial.create.modal.step1.subtitle",
            },
            CREATE: {
                title: `workspaces.trial.create.modal.step2.${this.state.selectedWorkspace.name}.title`,
                subtitle: `workspaces.trial.create.modal.step2.${this.state.selectedWorkspace.name}.subtitle`,
            },
        };
        const selectedStyle = modalStep === ModalStep.select ? step1 : step2;
        return (
            <WithTranslation>
                {(translate) => (
                    <ProModal
                        isOpen={isOpen}
                        onCloseClick={this.onCloseClick}
                        {...selectedStyle}
                    >
                        <div>
                            <TitleContainer>
                                <ModalTitle>{translate(modalText[modalStep].title)}</ModalTitle>
                            </TitleContainer>
                            <ModalSubtitle>{translate(modalText[modalStep].subtitle)}</ModalSubtitle>
                        </div>
                        {modalStep === ModalStep.select ? (
                            <StyledSwitcher
                                selectedIndex={this.state.selectedWorkspace.id}
                                onItemClick={this.onItemClick}
                            >
                                <StyledIconBoxWithTextSwitchItem customClass={"first"}>
                                    <IconContainer>
                                        <SWReactIcons iconName="investor" />
                                    </IconContainer>
                                    <Text>{translate("workspaces.trial.create.modal.step1.investor")}</Text>
                                </StyledIconBoxWithTextSwitchItem>
                                <StyledIconBoxWithTextSwitchItem>
                                    <IconContainer>
                                        <SWReactIcons iconName="marketer" />
                                    </IconContainer>
                                    <Text>{translate("workspaces.trial.create.modal.step1.marketer")}</Text>
                                </StyledIconBoxWithTextSwitchItem>
                                <StyledIconBoxWithTextSwitchItem>
                                    <IconContainer>
                                        <SWReactIcons iconName="sales" />
                                    </IconContainer>
                                    <Text>{translate("workspaces.trial.create.modal.step1.sales")}</Text>
                                </StyledIconBoxWithTextSwitchItem>
                            </StyledSwitcher>
                        ) : (
                            <div>
                                <StyledBgImage imgSrc={`images/workspace/optIn/marketing_workspace@2x.png`} />
                                <Footer>
                                    <Button
                                        onClick={this.createWorkspaceClick}
                                        type="primary"
                                    >
                                        <ButtonLabel>{translate("workspaces.trial.create.button")}</ButtonLabel>
                                    </Button>
                                </Footer>
                            </div>
                        )}
                    </ProModal>
                )}
            </WithTranslation>
        );
    }

    private onCloseClick = () => {
        this.context.track("Try workspace  wizard", "close", "Second screen");
        this.props.onCloseClick();
    };
}

const StyledSwitcher = styled(Switcher)`
    .first {
        margin-left: 0px;
    }
`;
StyledSwitcher.displayName = "StyledSwitcher";

const Text = styled.div`
    font-weight: 500;
    font-size: 14px;
    color: ${colorsPalettes.carbon["400"]};
    text-transform: capitalize;
`;

const StyledBgImage = styled(BgImage)`
    background-size: contain;
    height: 320px;
`;
StyledBgImage.displayName = "StyledBgImage";

const StyledIconBoxWithTextSwitchItem = styled(IconBoxWithTextSwitchItem)`
    border-width: 2px;
    ${Text} {
        position: relative;
        top: -8px;
    }
`;
StyledIconBoxWithTextSwitchItem.displayName = "StyledIconBoxWithTextSwitchItem";

export const Footer = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin-top: 24px;
`;
Footer.displayName = "Footer";

const IconContainer = styled.div`
    width: 110px;
    height: 110px;
`;

export const ModalTitle = styled.div.attrs<{ "data-automation"?: string }>({
    "data-automation": "select-workspace--trial-modal-title",
})`
    font-size: 16px;
    font-weight: 500;
    color: ${colorsPalettes.carbon["500"]};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;
ModalTitle.displayName = "ModalTitle";

export const ModalSubtitle = styled.div.attrs<{ "data-automation"?: string }>({
    "data-automation": "select-workspace--trial-modal-subtitle",
})`
    font-size: 14px;
    line-height: 24px;
    margin-bottom: 12px;
    max-width: 90%;
    color: ${colorsPalettes.carbon["300"]};
    margin-top: 12px;
`;
ModalSubtitle.displayName = "ModalSubtitle";

export const TitleContainer = styled.div`
    display: flex;
    align-items: center;
`;
TitleContainer.displayName = "TitleContainer";
